import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useDecrementCounter,
  useIncrementCounter,
  useSetCounterNumber,
} from './useAptCounter';

interface Props {
  value: number;
}

export function CounterControl({ value }: Props) {
  const { t } = useTranslation();
  const inc = useIncrementCounter();
  const dec = useDecrementCounter();
  const set = useSetCounterNumber();

  // Local draft for the "set to" input. We keep it in sync with `value` when
  // the user isn't editing, so a remote/optimistic update doesn't get blown
  // away by a stale local string.
  const [draft, setDraft] = useState(String(value));
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setDraft(String(value));
  }, [value, editing]);

  const submitDraft = () => {
    const n = Number.parseInt(draft, 10);
    if (Number.isNaN(n) || n < 0) {
      setDraft(String(value));
      setEditing(false);
      return;
    }
    set.mutate(n, { onSettled: () => setEditing(false) });
  };

  const disabled = inc.isPending || dec.isPending || set.isPending;

  return (
    <div className="flex flex-col items-center gap-5 px-6 py-8 sm:py-10">
      <div className="inline-flex items-center gap-5 sm:gap-7">
        <Button
          variant="secondary"
          size="xl"
          aria-label={t('aptCounter.decrement')}
          onClick={() => dec.mutate()}
          disabled={disabled || value === 0}
          className="h-14 w-14 rounded-full text-2xl sm:h-16 sm:w-16"
        >
          −
        </Button>
        <div
          aria-live="polite"
          className="min-w-[3ch] text-center font-semibold tabular-nums tracking-tight text-ink"
          style={{ fontSize: 'clamp(3.5rem, 12vw, 6rem)', lineHeight: 1 }}
        >
          {value}
        </div>
        <Button
          variant="primary"
          size="xl"
          aria-label={t('aptCounter.increment')}
          onClick={() => inc.mutate()}
          disabled={disabled}
          className="h-14 w-14 rounded-full text-2xl sm:h-16 sm:w-16"
        >
          +
        </Button>
      </div>

      <form
        className="flex w-full max-w-[260px] items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          submitDraft();
        }}
      >
        <Input
          inputMode="numeric"
          pattern="[0-9]*"
          value={draft}
          onChange={(e) => {
            setEditing(true);
            setDraft(e.target.value.replace(/[^0-9]/g, ''));
          }}
          onBlur={() => {
            if (draft === String(value)) setEditing(false);
          }}
          aria-label={t('aptCounter.setCounter')}
          className="text-center"
        />
        <Button type="submit" variant="secondary" disabled={disabled || draft === String(value)}>
          {t('aptCounter.setBtn')}
        </Button>
      </form>
    </div>
  );
}
